import EmailOutlined from '@mui/icons-material/EmailOutlined';
import SmsOutlined from '@mui/icons-material/SmsOutlined';
import { Chip, type ChipProps } from '@jotpsych/ui';
import {
  Alert,
  Box,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

import type {
  CommunicationEnvelopePayload,
  CommunicationStatus,
  TimelineEnvelope,
} from '../../types';

interface CommunicationCardProps {
  envelope: TimelineEnvelope;
  inThread?: boolean;
}

const CHANNEL_ICON: Record<string, React.ComponentType<{ sx?: object }>> = {
  sms: SmsOutlined,
  email: EmailOutlined,
};

const TERMINAL_FAILURE_STATUSES = new Set<CommunicationStatus>([
  'failed',
  'bounced',
  'dropped',
]);

/**
 * Communication envelope card. One per touchpoint — the same envelope
 * mutates in place as the lifecycle progresses (queued → sent →
 * delivered → opened). Status drives both the chip color and the
 * latest-stage timestamp shown in the meta strip.
 *
 * Failures (`failed` / `bounced` / `dropped`) get an inline Alert with
 * the error message so the clinician can see delivery problems without
 * digging in.
 */
export function CommunicationCard({ envelope, inThread = false }: CommunicationCardProps) {
  const payload = (envelope.payload ?? {}) as CommunicationEnvelopePayload;

  const channel = (envelope.subkind ?? payload.channel ?? '').toLowerCase();
  const Icon = CHANNEL_ICON[channel] ?? SmsOutlined;
  const status: CommunicationStatus = payload.status ?? 'queued';
  const isFailed = TERMINAL_FAILURE_STATUSES.has(status);

  const channelLabel = channel === 'email' ? 'Email' : 'SMS';
  const direction = payload.direction === 'inbound' ? 'inbound' : 'outbound';
  const verb = directionVerb(direction, payload.communication_type);
  const title = `${channelLabel} ${verb}`;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        borderColor: 'divider',
        bgcolor: inThread ? 'background.paper' : 'background.paper',
      }}
    >
      <Stack direction="row" spacing={1.25} sx={{ alignItems: "flex-start" }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 1,
            bgcolor: 'action.hover',
            color: 'text.secondary',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
          aria-hidden
        >
          <Icon sx={{ fontSize: 18 }} />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }} noWrap>
              {title}
            </Typography>
            <StatusChip status={status} />
          </Stack>
          <Stack
            direction="row"
            spacing={0.5}
            divider={<MetaDot />}
            sx={{alignItems: "center",  mt: 0.25, color: 'text.secondary' }}
          >
            <Typography variant="caption">
              {statusTimestampLabel(status, payload)}
            </Typography>
            {payload.communication_type && (
              <Typography variant="caption">
                {humanizeType(payload.communication_type)}
              </Typography>
            )}
          </Stack>
        </Box>
      </Stack>

      {isFailed && (payload.error_message || payload.error_code) && (
        <Alert severity="warning" sx={{ mt: 1, py: 0.5 }}>
          <Typography variant="caption">
            {payload.error_message ?? payload.error_code}
          </Typography>
        </Alert>
      )}
    </Paper>
  );
}

const STATUS_CHIP_COLOR: Record<CommunicationStatus, ChipProps['color']> = {
  queued: 'default',
  sent: 'info',
  delivered: 'success',
  opened: 'success',
  clicked: 'success',
  failed: 'error',
  bounced: 'error',
  dropped: 'error',
};

const STATUS_LABEL: Record<CommunicationStatus, string> = {
  queued: 'Queued',
  sent: 'Sent',
  delivered: 'Delivered',
  opened: 'Opened',
  clicked: 'Clicked',
  failed: 'Failed',
  bounced: 'Bounced',
  dropped: 'Dropped',
};

function StatusChip({ status }: { status: CommunicationStatus }) {
  return (
    <Chip
      size="small"
      label={STATUS_LABEL[status] ?? status}
      variant="soft"
      color={STATUS_CHIP_COLOR[status] ?? 'default'}
    />
  );
}

function statusTimestampLabel(
  status: CommunicationStatus,
  payload: CommunicationEnvelopePayload,
): string {
  // Prefer the most-progressed timestamp the payload carries.
  const candidate =
    payload.delivered_at ??
    payload.sent_at ??
    payload.failed_at ??
    payload.queued_at;
  if (!candidate) return STATUS_LABEL[status] ?? status;
  const stage = STATUS_LABEL[status] ?? status;
  return `${stage} ${formatRelative(candidate)}`;
}

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffMs = Date.now() - then;
  const minutes = Math.round(diffMs / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function directionVerb(
  direction: 'inbound' | 'outbound',
  type: string | null | undefined,
): string {
  const subject = humanizeType(type) || 'message';
  if (direction === 'outbound') {
    if (subject === 'message') return 'sent';
    return `— ${subject}`;
  }
  return `received ${subject !== 'message' ? subject : ''}`.trim();
}

function humanizeType(type: string | null | undefined): string {
  switch (type) {
    case 'appointment':
      return 'appointment reminder';
    case 'form':
      return 'form invite';
    case 'document':
      return 'document delivery';
    case 'lab':
      return 'lab notification';
    default:
      return type ?? '';
  }
}

function MetaDot() {
  return (
    <Typography
      component="span"
      variant="caption"
      sx={{ color: 'text.disabled', mx: 0.5 }}
      aria-hidden
    >
      ·
    </Typography>
  );
}
